// src/pages/TrueFalse.jsx
import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Truefalse.css';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const apiBase = "https://digital.tifleducation.com/admin/public/api";
const LOCAL_SELECTED_KEY = "selectedQuestionsByChapter";

const TrueFalse = () => {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [user, setUser] = useState({ id: null, name: '', email: '', password: '' });
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [tfLimit, setTfLimit] = useState(0);

  const toggleDropdown = () => setShowDropdown(prev => !prev);

  // Fetch questions and user data
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const token = localStorage.getItem('token');

        // Load True/False limit
        const regularForm = JSON.parse(localStorage.getItem("regularPaperForm") || "{}");
        const limit = Number(regularForm.true_false || 0);
        setTfLimit(limit);

        const getStoredId = (rawKey) => {
          const raw = localStorage.getItem(rawKey);
          if (!raw) return null;
          try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && parsed.id != null) return parsed.id;
            // If parsed is a number/string ID, return that
            return parsed;
          } catch {
            // not JSON, return raw string (likely an id string)
            return raw;
          }
        };
        // Get selected board/grade/subject
        const boardId = getStoredId('selectedBoardId') || getStoredId('selectedBoard') || getStoredId('board_id') || getStoredId('board');
        const gradeId = getStoredId('selectedGradeId') || getStoredId('selectedGrade') || getStoredId('grade_id') || getStoredId('grade');
        const subjectId = getStoredId('selectedSubjectId') || getStoredId('selectedSubject') || getStoredId('subject_id') || getStoredId('subject');

        if (!boardId || !gradeId || !subjectId) {
          toast.error('Missing selected board/grade/subject.');
          return;
        }

        // Fetch questions
        const res = await axios.post(
          `${apiBase}/papers/fetch-questions`,
          { board: Number(boardId), grade: Number(gradeId), subject: Number(subjectId) },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = res.data || {};
        const tfQs = [];
        Object.entries(data).forEach(([chapterId, chapterData]) => {
          const types = chapterData.types || {};
          const arr = types['True-False'] || [];
          (Array.isArray(arr) ? arr : []).forEach(q => {
            tfQs.push({ ...q, chapter_id: q.chapter_id ?? Number(chapterId) });
          });
        });

        setQuestions(tfQs);

        // Restore selected questions from localStorage
        const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
        const selectedMap = {};
        Object.entries(saved).forEach(([chapterId, types]) => {
          const arr = types.true_false || [];
          (Array.isArray(arr) ? arr : []).forEach(id => {
            selectedMap[Number(id)] = true;
          });
        });
        setSelectedQuestions(selectedMap);

      } catch (error) {
        console.error("Error fetching True/False questions:", error.response?.data || error);
        toast.error('Failed to fetch questions.');
      }
    };

    fetchQuestions();
  }, []);

  // Handle question selection
  const handleSelectQuestion = async (questionId, chapterId) => {
    const alreadySelected = !!selectedQuestions[questionId];
    const currentCount = Object.keys(selectedQuestions).length;

    if (!tfLimit) {
      toast.error("No True/False limit set. Please go back and set question counts.");
      return;
    }

    if (!alreadySelected && currentCount >= tfLimit) {
      toast.error(`You can only select up to ${tfLimit} True/False questions.`);
      return;
    }

    // Update local state
    setSelectedQuestions(prev => {
      const next = { ...prev };
      if (next[questionId]) delete next[questionId];
      else next[questionId] = true;
      return next;
    });

    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || "{}");
      const chapterKey = String(chapterId);
      if (!saved[chapterKey]) saved[chapterKey] = {};
      if (!Array.isArray(saved[chapterKey].true_false)) saved[chapterKey].true_false = [];

      const arr = saved[chapterKey].true_false;
      const idx = arr.findIndex(x => Number(x) === Number(questionId));
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(Number(questionId));

      saved[chapterKey].true_false = arr;
      localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(saved));

      // ✅ Send **all selected IDs** for that chapter to backend
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiBase}/papers/save-questions`,
        {
          chapter_id: Number(chapterId),
          type: "True-False",
          selected_questions: arr, // always array of numbers
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Selection saved");
    } catch (error) {
      console.error("Error saving True/False selection:", error.response?.data || error);
      toast.error("Failed to save selection");
    }
  };

  // Save profile
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiBase}/update-profile`,
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        <div className="true-header">
          <div className="true-welcome-text">
            <a onClick={() => navigate(-1)} className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="true-welcome-text">True - False</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
            </div>
          </div>
        </div>

        <div className="true-questions-container">
          {questions.length > 0 ? (
            questions.map((q, index) => (
              <div key={q.id} className="true-question-box">
                <div className="true-question">
                  <input
                    type="checkbox"
                    className="true-checkbox"
                    checked={!!selectedQuestions[q.id]}
                    onChange={() => handleSelectQuestion(q.id, q.chapter_id)}
                  />
                  <span className="question-text">
                    {index + 1}. {String(q.question || q.question_title || "").replace(/<\/?[^>]+(>|$)/g, "")}
                  </span>
                  <span className="true-label" style={{ marginLeft: '8px', color: '#555', fontStyle: 'italic' }}>
                    (True / False)
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="no-questions-container">
              <div className="no-questions-icon">📄</div>
              <h3>No True / False Questions Found</h3>
              <p style={{ color: '#666' }}>
                There are currently no True/False questions for this Board / Grade / Subject.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>

            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input
                type="text"
                value={user.name}
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input
                type="password"
                value={user.password}
                onChange={(e) => setUser({ ...user, password: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};

export default TrueFalse;
